import { Storage } from 'aws-amplify';
import { Skill } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { handleApiRequest } from '../api/utils/apiRequest';
import { getS3File } from '../utils/fileUtils';
import { getCurrentStage } from '../../configuration/aws-exports';
import { createStore } from './store-factory';
import { useNotificationStore } from './notification-store';

const SKILL_BUCKET = `finspace-skill-${getCurrentStage()}`;

interface SkillViewState {
  skill: Skill | null;
  isLoading: boolean;
  selectedPath: string | null;
  isSelectedDir: boolean;
  fileContent: string;
  isFileLoading: boolean;
  expanded: Set<string>;
  allPathsOverride: string[];
  newFiles: Set<string>;
  creating: { parentPath: string; type: 'file' | 'folder' } | null;
  contextMenu: { x: number; y: number; path: string; isDir: boolean } | null;
  versions: Skill[];
  isVersionsLoading: boolean;
  uploadModalVisible: boolean;
  uploadFile: File[];
  uploadS3Uri: string | null;
  isUploadLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;

  fetchSkill: (skillId: string, versionId: string) => Promise<void>;
  loadFile: (skillId: string, versionId: string, filePath: string) => Promise<void>;
  selectPath: (path: string, isDir: boolean) => void;
  toggleExpand: (path: string) => void;
  startCreate: (type: 'file' | 'folder', parentPath: string) => void;
  submitCreate: (name: string) => void;
  cancelCreate: () => void;
  deletePath: (path?: string, isDir?: boolean) => void;
  setContextMenu: (menu: { x: number; y: number; path: string; isDir: boolean } | null) => void;
  setFileContent: (content: string) => void;
  fetchVersions: (skillId: string, includeExperimental: boolean) => Promise<void>;
  openUploadModal: () => Promise<void>;
  closeUploadModal: () => void;
  setUploadFile: (files: File[]) => void;
  uploadNewVersion: (skillId: string) => Promise<Skill | null>;
  reset: () => void;
}

const initialState = {
  skill: null as Skill | null,
  isLoading: false,
  selectedPath: null as string | null,
  isSelectedDir: false,
  fileContent: '',
  isFileLoading: false,
  expanded: new Set<string>(),
  allPathsOverride: [] as string[],
  newFiles: new Set<string>(),
  creating: null as { parentPath: string; type: 'file' | 'folder' } | null,
  contextMenu: null as { x: number; y: number; path: string; isDir: boolean } | null,
  versions: [] as Skill[],
  isVersionsLoading: false,
  uploadModalVisible: false,
  uploadFile: [] as File[],
  uploadS3Uri: null as string | null,
  isUploadLoading: false,
  isUploading: false,
  uploadProgress: 0,
};

const getBasePaths = (skill: Skill | null): string[] =>
  skill?.fileStructure?.split('\n').filter(Boolean) || [];

const getCurrentPaths = (state: SkillViewState): string[] =>
  state.allPathsOverride.length > 0 ? state.allPathsOverride : getBasePaths(state.skill);

export const useSkillViewStore = createStore<SkillViewState>('skillView', (set, get) => ({
  ...initialState,

  fetchSkill: async (skillId: string, versionId: string) => {
    set({ ...initialState, isLoading: true });
    try {
      const response = await handleApiRequest(() => FinSpaceServiceApi.getSkill(skillId, versionId));
      const skill = response.skill || null;
      const basePaths = getBasePaths(skill);

      // Expand all directories
      const dirPaths = new Set<string>();
      for (const p of basePaths) {
        const parts = p.replace(/\/$/, '').split('/');
        let acc = '';
        for (let i = 0; i < parts.length - 1; i++) {
          acc += (i > 0 ? '/' : '') + parts[i];
          dirPaths.add(acc);
        }
        if (p.endsWith('/')) dirPaths.add(p.replace(/\/$/, ''));
      }

      set({ skill, isLoading: false, expanded: dirPaths });

      // Auto-select SKILL.md
      const skillMd = basePaths.find(p => !p.endsWith('/') && p.toLowerCase().endsWith('skill.md'));
      if (skillMd) {
        get().loadFile(skillId, versionId, skillMd);
      }
    } catch {
      set({ isLoading: false });
    }
  },

  loadFile: async (skillId: string, versionId: string, filePath: string) => {
    set({ selectedPath: filePath, isSelectedDir: false, isFileLoading: true, fileContent: '' });
    try {
      const prefix = `${skillId}/active/`;
      const result = await getS3File(prefix, filePath, SKILL_BUCKET);
      if (result.Body) {
        set({ fileContent: await (result.Body as Blob).text(), isFileLoading: false });
      } else {
        set({ isFileLoading: false });
      }
    } catch {
      set({ fileContent: 'Failed to load file content.', isFileLoading: false });
    }
  },

  selectPath: (path: string, isDir: boolean) => {
    if (isDir) {
      set({ selectedPath: path, isSelectedDir: true });
    } else if (get().newFiles.has(path)) {
      set({ selectedPath: path, isSelectedDir: false, fileContent: '' });
    }
    // For existing files, the component calls loadFile directly
  },

  toggleExpand: (path: string) => {
    const expanded = get().expanded;
    const next = new Set(expanded);
    next.has(path) ? next.delete(path) : next.add(path);
    set({ expanded: next });
  },

  startCreate: (type: 'file' | 'folder', parentPath: string) => {
    const expanded = get().expanded;
    const next = new Set(expanded);
    next.add(parentPath);
    set({ expanded: next, creating: { parentPath, type }, contextMenu: null });
  },

  submitCreate: (name: string) => {
    const { creating } = get();
    if (!creating) return;
    const { parentPath, type } = creating;
    const currentPaths = getCurrentPaths(get());

    if (type === 'file') {
      const newPath = `${parentPath}/${name}`;
      if (currentPaths.includes(newPath)) { set({ creating: null }); return; }
      const newFiles = new Set(get().newFiles);
      newFiles.add(newPath);
      set({
        allPathsOverride: [...currentPaths, newPath],
        newFiles,
        selectedPath: newPath,
        isSelectedDir: false,
        fileContent: '',
        creating: null,
      });
    } else {
      const newPath = `${parentPath}/${name}/`;
      if (currentPaths.includes(newPath)) { set({ creating: null }); return; }
      const expanded = new Set(get().expanded);
      expanded.add(newPath.replace(/\/$/, ''));
      set({
        allPathsOverride: [...currentPaths, newPath],
        expanded,
        creating: null,
      });
    }
  },

  cancelCreate: () => set({ creating: null }),

  deletePath: (targetPath?: string, targetIsDir?: boolean) => {
    const { selectedPath, isSelectedDir } = get();
    const path = targetPath || selectedPath;
    const isDir = targetIsDir ?? isSelectedDir;
    if (!path) return;

    const currentPaths = getCurrentPaths(get());
    const filtered = currentPaths.filter(p =>
      isDir ? !p.startsWith(path + '/') && p !== path + '/' : p !== path
    );

    const updates: Partial<SkillViewState> = { allPathsOverride: filtered, contextMenu: null };
    if (selectedPath === path || (isDir && selectedPath?.startsWith(path + '/'))) {
      updates.selectedPath = null;
      updates.fileContent = '';
    }
    set(updates);
  },

  setContextMenu: (menu) => set({ contextMenu: menu }),

  setFileContent: (content: string) => set({ fileContent: content }),

  fetchVersions: async (skillId: string, includeExperimental: boolean) => {
    set({ isVersionsLoading: true });
    try {
      const response = await handleApiRequest(() =>
        FinSpaceServiceApi.getSkillVersions(skillId, includeExperimental));
      set({ versions: (response.versions || []).reverse(), isVersionsLoading: false });
    } catch {
      set({ isVersionsLoading: false });
    }
  },

  openUploadModal: async () => {
    set({ isUploadLoading: true });
    try {
      const response = await handleApiRequest(() => FinSpaceServiceApi.getTransferS3URI());
      set({ uploadS3Uri: response.s3URI, uploadModalVisible: true, isUploadLoading: false });
    } catch {
      set({ isUploadLoading: false });
      useNotificationStore.getState().error('Failed to initialize upload.');
    }
  },

  closeUploadModal: () => set({
    uploadModalVisible: false, uploadFile: [], uploadS3Uri: null, isUploading: false, uploadProgress: 0,
  }),

  setUploadFile: (files: File[]) => set({ uploadFile: files }),

  uploadNewVersion: async (skillId: string) => {
    const { uploadS3Uri, uploadFile } = get();
    if (!uploadS3Uri || uploadFile.length === 0) return null;

    set({ isUploading: true, uploadProgress: 0 });
    try {
      const key = uploadS3Uri.split('://')[1].split('/').slice(1).join('/');
      await Storage.put(key, uploadFile[0], {
        progressCallback: (progress) => {
          set({ uploadProgress: (progress.loaded / progress.total) * 100 });
        },
        customPrefix: { public: '' },
      });

      const response = await handleApiRequest(() =>
        FinSpaceServiceApi.updateSkillFromZip(skillId, uploadS3Uri));

      const newSkill = response.skill;
      set({
        uploadModalVisible: false, uploadFile: [], uploadS3Uri: null, isUploading: false, uploadProgress: 0,
      });
      useNotificationStore.getState().success('New version uploaded successfully!');

      return newSkill || null;
    } catch {
      set({ isUploading: false });
      useNotificationStore.getState().error('Upload failed. Please try again.');
      return null;
    }
  },

  reset: () => set(initialState),
}));
